import * as React from "react";
import classes from "./BulkUpload.module.css";
import Grid from "@mui/material/Grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import { format } from "date-fns";
import PropTypes from "prop-types";

import excel from "exceljs";

import {
  getAllSystemTypes,
  getAllSystemsBySystemTypeID,
  getAllSubSystemsBySystemTypeID,
  validateExcelData,
  insertUploadedFile,
} from "services/centerPointUserServices";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "components/AccordionComponents/AccordionComponents";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import moment from "moment";
import exportAsCsv from "utils/exportAsCsv";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { red } from "@mui/material/colors";

const typographyStyle = {
  marginTop: 1,
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  letterSpacing: "0.10rem",
  fontWeight: "bold",
  color: "#3F5765",
};

const DateEditor = ({
  value,
  editorCallback,
  rowData,
  field,
  errorToolTip,
  setErrorTooltip,
  errorRowsRef,
}) => {
  const parsed = value ? new Date(value) : null;
  const id = rowData?.id;

  const [localError, setLocalError] = React.useState(
    errorToolTip?.[id]?.[field] || "",
  );

  React.useEffect(() => {
    setLocalError(errorToolTip?.[id]?.[field] || "");
  }, [errorToolTip, id, field]);

  const handleChange = (e) => {
    const date = e.value;
    if (!date) {
      setLocalError("");
      editorCallback(null);
      return;
    }

    const formatted = format(date, "yyyy-MM-dd'T'HH:mm:ss");
    const otherField =
      field === "effectiveDate" ? "expireDate" : "effectiveDate";

    const liveRow = errorRowsRef?.current?.find((r) => r.id === id);
    const otherVal = liveRow?.[otherField] ?? rowData?.[otherField];

    if (otherVal) {
      const effectiveDate =
        field === "effectiveDate" ? date : new Date(otherVal);
      const expireDate = field === "expireDate" ? date : new Date(otherVal);
      const diff = (expireDate - effectiveDate) / (1000 * 60 * 60 * 24);

      let errorMsg = "";
      if (diff < 0) {
        errorMsg =
          field === "effectiveDate"
            ? "Effective date must be before expire date."
            : "Expire date must be after effective date.";
      } else if (diff > 366) {
        errorMsg =
          field === "effectiveDate"
            ? "Effective date is too early."
            : "Expire date can't be more than 1 year from effective date.";
      }

      setLocalError(errorMsg);

      setErrorTooltip((prev) => {
        const updated = { ...prev };
        if (errorMsg) {
          updated[id] = { ...updated[id], [field]: errorMsg };
        } else {
          if (updated[id]) {
            delete updated[id][field];
            if (Object.keys(updated[id]).length === 0) delete updated[id];
          }
          if (updated[id]) {
            delete updated[id][otherField];
            if (Object.keys(updated[id]).length === 0) delete updated[id];
          }
        }
        return updated;
      });
    }

    editorCallback(formatted);
  };

  const hasError = localError;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Calendar
        value={parsed}
        onChange={handleChange}
        dateFormat="mm/dd/yy"
        showIcon
        showButtonBar
        inputStyle={
          hasError
            ? {
                borderColor: "#ef4444",
                background: "#fff5f5",
                width: "98px",
                fontSize: "12px",
              }
            : { width: "98px", fontSize: "12px" }
        }
      />
      {hasError && (
        <small style={{ color: "#dc2626", fontSize: "10px", marginTop: "2px" }}>
          {localError}
        </small>
      )}
    </div>
  );
};

DateEditor.propTypes = {
  value: PropTypes.string,
  editorCallback: PropTypes.func.isRequired,
  rowData: PropTypes.object,
  field: PropTypes.string.isRequired,
  errorToolTip: PropTypes.object,
  setErrorTooltip: PropTypes.func,
  errorRowsRef: PropTypes.object,
};

const BulkUpload = () => {
  const [loader, setLoader] = React.useState(false);
  const [validationLoader, setValidationLoader] = React.useState(false);
  const [expandedPanel1, setExpandedPanel1] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [isChecked, setIsChecked] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [file, setFile] = React.useState(null);
  const [errorRows, setErrorRows] = React.useState([]);
  const [parsedData, setParsedData] = React.useState([]);
  const [subSystemId, setSubSystemId] = React.useState(0);
  const [subSystemArray, setSubSystemArray] = React.useState([]);
  const [systemTypesArray, setSystemTypesArray] = React.useState([]);
  const [systemTypeId, setSystemTypeId] = React.useState(0);
  const [system, setSystem] = React.useState(0);
  const [systemsArray, setSystemsArray] = React.useState([]);
  const [fileKey, setFileKey] = React.useState(0);
  const [editedRowIds, setEditedRowIds] = React.useState(new Set());
  const [showConfirmationPopup, setShowConfirmationPopup] =
    React.useState(false);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });
  const fileInputRef = React.useRef(null);
  const requiredFields = ["oracleId", "logonId", "effectiveDate", "expireDate"];
  const [errorToolTip, setErrorTooltip] = React.useState({});
  const errorRowsRef = React.useRef([]);
  React.useEffect(() => {
    errorRowsRef.current = errorRows;
  }, [errorRows]);

  React.useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoader(true);
      }

      try {
        const systemTypesResponse = await getAllSystemTypes();

        if (cancelled) return;

        if (systemTypesResponse?.data?.message === "Success") {
          if (!cancelled) {
            setSystemTypesArray(systemTypesResponse?.data?.data);
          }
        } else {
          if (!cancelled) {
            errorHandler(true, "Could not fetch system types", "error");
          }
        }
      } catch (error) {
        if (!cancelled) {
          errorHandler(true, "Something went wrong !", "error");
        }
      } finally {
        if (!cancelled) {
          setLoader(false);
        }
      }
    };

    const fetchDataAndLog = async () => {
      await fetchData();

      if (!cancelled) {
        applicationUsageLog(
          localStorage.getItem("User"),
          localStorage.getItem("EmailId"),
          "CenterPoint",
          "Bulk Upload",
        );
      }
    };

    fetchDataAndLog();

    return () => {
      cancelled = true;
    };
  }, []);

  const columns = [
    {
      field: "oracleId",
      header: "Oracle ID",
      sortable: true,
      filter: true,
      filterPlaceholder: "Search by Oracle ID",
      headerAlign: "left",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "logonId",
      header: "Logon ID",
      sortable: true,
      filter: true,
      filterPlaceholder: "Search by Logon ID",
      headerAlign: "left",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "effectiveDate",
      header: "Effective Date",
      sortable: true,
      filter: true,
      filterPlaceholder: "Search by Effective Date",
      headerAlign: "left",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => {
        const date = rowData.effectiveDate
          ? new Date(rowData.effectiveDate)
          : null;
        return date ? format(date, "MM/dd/yyyy") : "";
      },
      editor: (options) =>
        isEditableRow(options.rowData) ? (
          <DateEditor
            value={options.value}
            editorCallback={options.editorCallback}
            rowData={options.rowData}
            field="effectiveDate"
            errorToolTip={errorToolTip}
            setErrorTooltip={setErrorTooltip}
            errorRowsRef={errorRowsRef}
          />
        ) : null,
      onCellEditComplete: (e) => {
        if (!isEditableRow(e.rowData)) return;
        const newVal = e.newValue ?? e.newRowData?.effectiveDate;
        if (newVal === e.rowData.effectiveDate) return;
        setErrorRows((prev) =>
          prev.map((row) =>
            row.id === e.rowData.id ? { ...row, effectiveDate: newVal } : row,
          ),
        );
        setEditedRowIds((prev) => new Set(prev).add(e.rowData.id));
      },
    },
    {
      field: "expireDate",
      header: "Expire Date",
      sortable: true,
      filter: true,
      filterPlaceholder: "Search by Expire Date",
      headerAlign: "left",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => {
        const date = rowData.expireDate ? new Date(rowData.expireDate) : null;
        return date ? format(date, "MM/dd/yyyy") : "";
      },
      editor: (options) =>
        isEditableRow(options.rowData) ? (
          <DateEditor
            value={options.value}
            editorCallback={options.editorCallback}
            rowData={options.rowData}
            field="expireDate"
            errorToolTip={errorToolTip}
            setErrorTooltip={setErrorTooltip}
            errorRowsRef={errorRowsRef}
          />
        ) : null,
      onCellEditComplete: (e) => {
        if (!isEditableRow(e.rowData)) return;
        const newVal = e.newValue ?? e.newRowData?.expireDate;
        if (newVal === e.rowData.expireDate) return;
        setErrorRows((prev) =>
          prev.map((row) =>
            row.id === e.rowData.id ? { ...row, expireDate: newVal } : row,
          ),
        );
        setEditedRowIds((prev) => new Set(prev).add(e.rowData.id));
      },
    },
    {
      field: "message",
      header: "Message",
      sortable: true,
      filter: true,
      filterPlaceholder: "Search by Message",
      headerAlign: "left",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <Typography
          sx={{
            ...typographyStyle,
            fontWeight: "normal",
            color: red[700],
            fontSize: "0.85rem",
          }}
        >
          {rowData.message}
        </Typography>
      ),
    },
  ];

  const hasDateErrors = errorRows?.some(
    (row) =>
      errorToolTip[row.id]?.effectiveDate || errorToolTip[row.id]?.expireDate,
  );

  React.useEffect(() => {
    if (subSystemId && system && systemTypeId) {
      setError(false);
      setErrorMessage("");
    }
  }, [subSystemId, system, systemTypeId]);

  const handleFileButtonClick = () => {
    if (subSystemId && system && systemTypeId) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
      }
    } else {
      setError(true);
      setErrorMessage(
        "Please ensure that you have selected options from all the dropdown menus before uploading the file.",
      );
    }
  };

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: open,
      message: message,
      severity: severity,
    });
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const validate = async (payload) => {
    setValidationLoader(true);
    try {
      const response = await validateExcelData(payload, subSystemId);
      if (response?.data?.message === "Success") {
        setError(false);
        if (response?.data?.data[0]?.IsError) {
          setErrorRows(
            response?.data?.data?.map((item, index) => {
              return {
                ...item,
                id: index + 1,
              };
            }),
          );
        } else {
          errorHandler(true, "File validated succesfully", "success");
          setErrorRows([]);
        }
      } else if (response.data.statusCode === 404) {
        setError(true);
        setErrorMessage(response?.data?.message);
      } else {
        errorHandler(
          true,
          "Could not validate data in the uploaded file",
          "error",
        );
      }
    } catch {
      errorHandler(true, "Something went wrong !", "error");
    }
    setValidationLoader(false);
  };

  const handleSystemTypeChange = async (systemTypeId, mode) => {
    setLoader(true);
    setError(false);
    setSystemTypeId(() => systemTypeId);
    try {
      const response = await getAllSystemsBySystemTypeID(systemTypeId);
      if (response?.data?.message === "Success") {
        setSystemsArray(() => response?.data?.data);
        setSystem(0);
        setSubSystemArray([]);
        setSubSystemId(0);
      } else {
        errorHandler(true, "Could not fetch systems", "error");
      }
    } catch {
      errorHandler(true, "Something went wrong !", "error");
    }
    setLoader(false);
  };

  const handleSystemChange = async (systemId, mode) => {
    setLoader(true);
    setError(false);
    setSystem(systemId);
    try {
      const response = await getAllSubSystemsBySystemTypeID(
        systemTypeId,
        systemId,
      );
      if (response?.data?.message === "Success") {
        setSubSystemArray(response?.data?.data);
      } else {
        errorHandler(true, "Could not fetch sub systems", "error");
      }
    } catch {
      errorHandler(true, "Something went wrong !", "error");
    }
    setLoader(false);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    const allowedExtensions = [".xlsx", ".xls"];
    const fileExtension = uploadedFile?.name?.slice(
      uploadedFile.name.lastIndexOf("."),
    );

    if (!allowedExtensions.includes(fileExtension?.toLowerCase())) {
      setError(true);
      setErrorMessage(" Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setFile(uploadedFile);
    setError(false);

    const reader = new FileReader();

    reader.onload = handleWorkbookLoad;

    reader.readAsArrayBuffer(uploadedFile);
  };

  const handleWorkbookLoad = async (e) => {
    try {
      const binaryString = e.target.result;
      const workbook = await loadWorkbook(binaryString);
      const worksheet = workbook.getWorksheet(1);
      const { rows, areEqual } = await processWorksheet(worksheet);
      setParsedData(rows);
      if (areEqual) validate(rows);
    } catch {
      setError(true);
      setErrorMessage("Please follow the valid template");
    }
  };

  const loadWorkbook = async (binaryString) => {
    const workbook = new excel.Workbook();
    await workbook.xlsx.load(binaryString);
    return workbook;
  };

  const processWorksheet = async (worksheet) => {
    const rows = [];
    const columnData = [];

    let areEqual = false;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 2) {
        Object.values(row?.values).forEach((column) => {
          if (column !== 1 || column !== 2)
            columnData.push(column?.charAt(0).toLowerCase() + column?.slice(1));
        });
        areEqual =
          JSON.stringify(columnData.map((column) => column.toLowerCase())) ===
          JSON.stringify(requiredFields.map((field) => field.toLowerCase()));
        if (areEqual) setError(false);
        else {
          setError(true);
          setErrorMessage("Please follow the valid template");
        }
      } else if (rowNumber === 1 && row?.values[3] !== "mm/dd/yyy") {
        setError(true);
        setErrorMessage("Please follow the valid template");
      } else {
        rows.push({
          id: rowNumber - 2,
          oracleId: row?.values[1]?.text
            ? row?.values[1]?.text?.toString()
            : row?.values[1]?.toString(),
          logonId: row?.values[2]?.text
            ? row?.values[2]?.text?.toString()
            : row?.values[2]?.toString(),
          effectiveDate: moment(row?.values[3]).toISOString(),
          expireDate: moment(row?.values[4]).toISOString(),
        });
      }
    });

    return { rows, areEqual };
  };

  const handleDownload = () => {
    const filePath =
      process.env.PUBLIC_URL + "/assets/files/CenterPointBulkUploadSample.xlsx";

    const link = document.createElement("a");
    link.href = filePath;
    link.setAttribute("download", "Sample File.xlsx");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterRows = () => {
    errorRows.forEach(({ id, ...rest }) => rest);
    if (errorRows?.length > 0) {
      return errorRows;
    } else {
      return parsedData.map(({ id, ...rest }) => rest);
    }
  };

  const handleOk = async () => {
    setLoader(true);
    const rowsWithoutOracleId = errorRows.filter(
      (row) => !row.oracleId?.trim(),
    );
    const employeeUploadData = filterRows();
    if (rowsWithoutOracleId.length > 0) {
      setLoader(false);
      errorHandler(
        true,
        "Data couln't be inserted Please Revalidate the data",
        "error",
      );
      return;
    }
    if (!systemTypeId || !system || !subSystemId) {
      setError(true);
      setErrorMessage("Please fill all the fields");
    } else if (employeeUploadData.length === 0) {
      setError(true);
      setErrorMessage("Please attach a file with data to upload");
    } else {
      setError(false);

      const payload = {
        systemTypeId: Number(systemTypeId),
        systemId: Number(system) || 0,
        subSystemId: Number(subSystemId) || 0,
        loggedOnUsername: localStorage?.getItem("User"),
        employeeData: employeeUploadData,
      };

      try {
        const response = await insertUploadedFile(payload);
        if (response?.data?.message === "Success") {
          setEditedRowIds(new Set());
          errorHandler(
            true,
            errorRows?.length
              ? "Data has been updated Successfully"
              : "Data has been inserted successfully",
            "success",
          );
          handleCancel();
        } else {
          errorHandler(
            true,
            "Could not insert the uploaded file data",
            "error",
          );
        }
      } catch {
        errorHandler(true, "Something went wrong", "error");
      }
    }
    setLoader(false);
  };

  const handleCancel = () => {
    setSystemTypeId(0);
    setSubSystemId(0);
    setSystem(0);
    setParsedData([]);
    setErrorRows([]);
    setFile(null);
    setFileKey((prevKey) => prevKey + 1);
    setError(false);
    setErrorMessage("");
  };

  const processRowUpdate = (newRow, oldRow) => {
    setErrorRows((prev) =>
      prev.map((row) => (row.id === newRow.id ? newRow : row)),
    );
    setEditedRowIds((prev) => {
      const updated = new Set(prev);
      updated.add(newRow.id);
      return updated;
    });
    return newRow;
  };

  const isEditableRow = (row) => {
    const hasOracle = !!row.oracleId;
    return hasOracle;
  };

  const disableButton =
    error ||
    validationLoader ||
    loader ||
    (errorRows?.length > 0 && !isChecked);

  return (
    <>
      {loader && <BackdropComponent />}
      <div>
        <Accordion
          expanded={expandedPanel1}
          onChange={() => setExpandedPanel1((prev) => !prev)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <div className={classes.headingContainer}>
              <span className={classes.filterText}>Bulk File Upload</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              spacing={2}
              wrap="wrap"
              sx={{ marginLeft: 1, marginTop: 0.5 }}
            >
              {/* First Row */}
              <Grid item xs={1}>
                <Typography sx={{ ...typographyStyle }}>
                  System Type:
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <Select
                    placeholder="System Type"
                    value={systemTypeId}
                    onChange={(e) =>
                      handleSystemTypeChange(e.target.value, "onChange")
                    }
                  >
                    <MenuItem value={0}>-- Select an option --</MenuItem>
                    {systemTypesArray?.map((systemTypes) => (
                      <MenuItem
                        key={systemTypes?.CP_SystemTypeId}
                        value={systemTypes?.CP_SystemTypeId}
                      >
                        {systemTypes?.Name} ({systemTypes?.Code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Blank Row */}
              <Grid item xs={8} />

              {/* Second Row */}
              <Grid item xs={1}>
                <Typography sx={{ ...typographyStyle }}>Systems:</Typography>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <Select
                    placeholder="System Location"
                    value={system}
                    onChange={(e) =>
                      handleSystemChange(e.target.value, "onChange")
                    }
                  >
                    <MenuItem value={0}>-- Select an option --</MenuItem>
                    {systemsArray?.map((systemLocation) => (
                      <MenuItem
                        key={systemLocation?.CP_SystemId}
                        value={systemLocation?.CP_SystemId}
                      >
                        {systemLocation?.Name} ({systemLocation?.Code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Blank Row */}
              <Grid item xs={8} />

              {/* Third Row */}
              <Grid item xs={1}>
                <Typography sx={{ ...typographyStyle }}>Subsystem:</Typography>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <Select
                    placeholder="Sub System"
                    value={subSystemId}
                    onChange={(e) => {
                      setError(false);
                      setSubSystemId(e.target.value);
                    }}
                  >
                    <MenuItem value={0}>-- Select an option --</MenuItem>
                    {subSystemArray?.map((subSystem) => (
                      <MenuItem
                        key={subSystem?.CP_SubSystemId}
                        value={subSystem?.CP_SubSystemId}
                      >
                        {subSystem?.SubSystemName} ({subSystem?.SubSystemCode})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {errorRows?.length > 0 && (
                <>
                  {/* Blank Row */}
                  <Grid item xs={8} />

                  <Grid item xs={2}>
                    <Typography
                      sx={{
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                        letterSpacing: "0.10rem",
                        fontWeight: "bold",
                        color: "black",
                      }}
                    >
                      Update existing entries if match found
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Tooltip
                      title={
                        hasDateErrors
                          ? "Please Fix the Date Errors(Effective/Expire) before continuing"
                          : ""
                      }
                      arrow
                      disableHoverListener={!hasDateErrors}
                    >
                      <span>
                        <Checkbox
                          checked={isChecked}
                          disabled={hasDateErrors}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              setShowConfirmationPopup(true);
                            } else {
                              setIsChecked(checked);
                            }
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                </>
              )}
            </Grid>
            <div className={classes.templateDownload}>
              <CustomButton
                variant="outlined"
                sx={{ color: "#3299FF" }}
                onClick={handleDownload}
              >
                Download Template
              </CustomButton>{" "}
              <p className={classes.note}>
                <i>
                  <b>Note:</b> Please enter the dates in mm/dd/yyyy format in
                  the excel file.
                </i>
              </p>
            </div>

            <div className={classes.chooseFile}>
              <CustomButton
                sx={{ backgroundColor: "#3299FF" }}
                variant="contained"
                onClick={handleFileButtonClick}
                disabled={validationLoader}
              >
                Choose File
              </CustomButton>
              <input
                key={fileKey}
                type="file"
                id="upload-file"
                ref={fileInputRef}
                className={classes.fileInput + " " + classes.defaultInput}
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
              />
              <span>{file ? file.name : "No file chosen"}</span>
            </div>
            {error && <p className={classes.error}>{errorMessage}</p>}
            {validationLoader && (
              <Box sx={{ width: "100%", p: 2 }}>
                <LinearProgress />
              </Box>
            )}

            <div className={classes.buttonContainer}>
              <CustomButton
                variant="contained"
                sx={{ backgroundColor: "#3299FF" }}
                disabled={disableButton}
                onClick={handleOk}
              >
                OK
              </CustomButton>
              <CustomButton
                variant="outlined"
                sx={{ color: "#3299FF" }}
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </CustomButton>
            </div>
            {errorRows?.length > 0 && (
              <Grid item xs={6}>
                <Typography
                  sx={{
                    marginTop: 1,
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    letterSpacing: "0.10rem",
                    fontWeight: "bold",
                    color: "#5dd55d",
                  }}
                >
                  * You can edit the Effective and Expire Dates before going to
                  Submit
                </Typography>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
        <hr />
        {errorRows?.length > 0 && (
          <>
            <div className={classes.exportContainer}>
              <CustomButton
                variant="contained"
                sx={{ backgroundColor: "#3299FF" }}
                className={classes.downloadButton}
                startIcon={<DownloadIcon />}
                onClick={() =>
                  exportAsCsv(errorRows, columns, "CPSearchEmployee")
                }
              >
                EXPORT
              </CustomButton>
            </div>

            <div className={classes.dataGrid}>
              <PrimeReactDataGrid
                columns={columns}
                value={errorRows}
                editMode="cell"
                removableSort
                resizableColumns
                showGridlines
                paginator
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
                filterprops={{
                  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                  oracleId: {
                    operator: FilterOperator.AND,
                    constraints: [
                      { value: null, matchMode: FilterMatchMode.CONTAINS },
                    ],
                  },
                  logonId: {
                    operator: FilterOperator.AND,
                    constraints: [
                      { value: null, matchMode: FilterMatchMode.CONTAINS },
                    ],
                  },
                  effectiveDate: {
                    operator: FilterOperator.AND,
                    constraints: [
                      { value: null, matchMode: FilterMatchMode.CONTAINS },
                    ],
                  },
                  expireDate: {
                    operator: FilterOperator.AND,
                    constraints: [
                      { value: null, matchMode: FilterMatchMode.CONTAINS },
                    ],
                  },
                  message: {
                    operator: FilterOperator.AND,
                    constraints: [
                      { value: null, matchMode: FilterMatchMode.CONTAINS },
                    ],
                  },
                }}
                globalFilterFields={[
                  "oracleId",
                  "logonId",
                  "effectiveDate",
                  "expireDate",
                  "message",
                ]}
                rowClassName={(rowData) => {
                  const errors = errorToolTip[rowData.id];
                  const isEdited = editedRowIds.has(rowData.id);
                  const hasDateError =
                    errors?.effectiveDate || errors?.expireDate;
                  if (!isEditableRow(rowData)) return "no-oracle-id";
                  if (hasDateError) return "red-row";
                  if (isEdited) return "row-edited";
                  return "";
                }}
              />
            </div>
          </>
        )}

        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      </div>

      <Dialog
        open={showConfirmationPopup}
        onClose={() => setShowConfirmationPopup(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-papaer": {
            width: "500px",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            marginLeft: "25%",
          }}
        >
          {" "}
          Override Confirmation{" "}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The following rows will be overriden:
          </Typography>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Oracle ID</b>
                </TableCell>
                <TableCell>
                  <b>Logon ID</b>
                </TableCell>
                <TableCell>
                  <b>Effective Date</b>
                </TableCell>
                <TableCell>
                  <b>Expire Date</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errorRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.oracleId || "-"}</TableCell>
                  <TableCell>{row.logonId || "-"}</TableCell>
                  <TableCell>
                    {row.effectiveDate?.split("T")[0] || "-"}
                  </TableCell>
                  <TableCell>{row.expireDate?.split("T")[0] || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmationPopup(false);
              setIsChecked(false);
            }}
          >
            {" "}
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowConfirmationPopup(false);
              setIsChecked(true);
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUpload;
